<script lang="ts">
  import { cn } from '@/lib/utils';
  import { Button } from '@/lib/components/ui/button';
  import type { Exercise } from '@/Common/ActivityFunctions';
  import ActivityCardTitleDetails from './ActivityCardTitleDetails.svelte';
  import ActivityCardDetails from './ActivityCardDetails.svelte';
  import ActivityCardImages from './ActivityCardImages.svelte';
  import ActivityCardActions from './ActivityCardActions.svelte';
  import {
    activityCardImages,
    activityDetailsLink,
    cardLayoutForWidth,
    detailsLayoutForCard,
    type DashboardActivity,
  } from './activityCardUtils';

  export let exercise: DashboardActivity;
  export let onViewDetails: ((exercise: Exercise) => void) | null = null;
  export let onDelete: (exercise: Exercise) => void = () => {};
  export let onChange: (exercise: Exercise, changes: Partial<Exercise>) => void = () => {};
  export let onRefresh: () => void = () => {};
  /** Coach/squad dashboards list several athletes, so each card names whose ride it is. */
  export let showOwner: boolean = false;

  // Same container-query trick TrainingCardLong uses: the card is mounted at wildly different
  // widths (full page, my-fitness tab, narrow phone) and media queries only know about the viewport.
  // Each tier is laid out for its own size rather than being the previous one reflowed -- see
  // cardLayoutForWidth. The action bar spans the bottom of the card at every tier.
  let containerSize: ResizeObserverSize[] | null = null;
  let containerWidth: number = 0;
  $: if (containerSize && containerSize[0]) containerWidth = containerSize[0].inlineSize;
  $: layout = cardLayoutForWidth(containerWidth);
  /** Below `md` there isn't room for a useful image column, so the image goes above the details. */
  $: stacked = layout === 'xs' || layout === 'sm';
  /**
   * At `md` there is room for the image beside the *title* but not beside the details as well: an
   * image column that tall makes the details column too narrow to read, and pads itself out with
   * grey. So the card splits horizontally instead -- image and title across the top, details across
   * the full width underneath -- which also gives the details more room than the tier implies.
   */
  $: splitRows = layout === 'md';
  $: detailsLayout = detailsLayoutForCard(layout, containerWidth);

  $: detailsLink = activityDetailsLink(exercise);
  $: images = activityCardImages(exercise);
</script>

<div bind:borderBoxSize={containerSize} class="text-sm">
  {#if splitRows}
    <div class="flex flex-col gap-4">
      <div class="flex flex-row items-start gap-4">
        <!-- Image: same anchor position as the card it replaces. Owns its own sizing and paging. -->
        <ActivityCardImages {images} medal={exercise?.medal} {layout} />

        <div class="flex-1 min-w-0">
          <ActivityCardTitleDetails {exercise} {layout} {showOwner} besideImage />
        </div>
      </div>

      <!-- The details get the whole card's width here, not the tier's share of it, so they are laid
           out for what they actually have -- see detailsLayoutForCard. -->
      <ActivityCardDetails {exercise} layout={detailsLayout} />
    </div>
  {:else}
    <div class={cn('flex gap-4', stacked ? 'flex-col' : 'flex-row items-stretch')}>
      <!-- Image: same anchor position as the card it replaces. Owns its own sizing and paging. -->
      <ActivityCardImages {images} medal={exercise?.medal} {layout} />

      <!-- The tier comes from the whole card's width here, because the image column is part of what
           has to fit; elsewhere the two halves measure themselves. -->
      <div class={cn('flex-1 min-w-0 flex flex-col', layout === 'xs' ? 'gap-3' : 'gap-2.5')}>
        <ActivityCardTitleDetails {exercise} {layout} {showOwner} />
        <ActivityCardDetails {exercise} {layout} />
      </div>
    </div>
  {/if}

  <!-- Action bar: along the bottom at every tier. -->
  <div
    class={cn(
      'mt-3 pt-3 border-solid border-0 border-t border-border flex flex-row flex-wrap items-center gap-x-3 gap-y-2',
      layout === 'xs' ? 'justify-center' : 'justify-between'
    )}
  >
    <div class={cn('flex flex-row flex-wrap items-center gap-2', layout === 'xs' ? 'justify-center' : '')}>
      {#if onViewDetails}
        <Button size="sm" variant="outline" on:click={(e) => { e.stopPropagation(); onViewDetails(exercise); }}>
          <i class="fa-regular fa-chart-line text-xs mr-1" aria-hidden="true"></i>Quick View
        </Button>
      {/if}
      <Button size="sm" variant="outline" href={detailsLink} target="activity" class="text-foreground no-underline" on:click={(e) => e.stopPropagation()}>
        <i class="fa-regular fa-arrow-up-right-from-square text-xs mr-1" aria-hidden="true"></i>Open Activity
      </Button>
      <slot />
    </div>
    <ActivityCardActions bind:exercise {onDelete} {onChange} {onRefresh} />
  </div>
</div>
